import React from "react";
import { ActivityIndicator, Image, Pressable, StyleSheet, TouchableOpacity, View } from "react-native";
import { useSelector } from "react-redux";
import { useIsFocused, useNavigation } from "@react-navigation/native";
import moment from "moment";

import { tBrandRetailerConnectionResponse, tVisualConnectionStatus } from "tmt_api/src/types/types.validator";
import * as mappers from "tmt_api/src/shared/mappers";

import * as utilsDb from "src/db/utils.db";
import * as connectionDb from "src/db/connection.db";
import { PdStack, PdTag, PdTagLabel, PdText } from "src/components/layout";
import {
  PdBadge,
  PdBadgeLabel,
  PdTab,
  PdTabLabel,
  PdTable,
  PdTableCell,
  PdTableCellText,
  PdTableMobileItemSeparator,
  PdTablePaginationFooter,
  PdTableRow,
  PdTableRowMobile,
  PdTableSortableHeaders,
  tPdBadgeColorScheme,
} from "src/components/v2/layout";
import { tColumn, tRowInstance, useTable } from "src/hooks/table";
import { useDebounce, useIsMobile, useUserProfile } from "src/hooks/ui";
import { kColors } from "src/kColors";
import { kStyleConst } from "src/kStyle";
import { formatPercentage } from "src/util/gFormatting";
import { Link, PdCopy, PdSettings, PdStorefront, PdUserAdd } from "src/components/Icons";
import { PdButton, PdButtonLabel } from "src/components/v2/forms/button";
import { useAddRetailerModal, useAdminConnectCustomers, useInviteRetailersModal } from "src/hooks/modals";
import { kRoute } from "src/kRoute";
import { tPressableState } from "src/components/types";
import { useAppDispatch } from "src/redux";
import { generateCSVFromTableInstance, getHiddenColumnsFromConfig } from "src/util/table";
import { PdSearchInput } from "src/components/v2/ui";

import { formatDatabaseConnectionStatus } from "tmt_api/src/util/formatters";
import { kTablePageSize } from "src/util/constants";
import {
  changeFilterConnectionSource,
  changeFilterConnectionStatus,
  changeFilterNextPage,
  changeFilterPrevPage,
  changeFilterTerm,
  resetFilters,
} from "src/redux/filters/actions";
import { PdCenter } from "src/components/v2/ui/core";
import { PdFormLabel } from "src/components/v2/forms/control";
import { PdPopover, PdPopoverContent, PdPopoverTrigger } from "src/components/v2/overlay";
import PopoverContentTableColumns from "src/components/connected/PopoverContentTableColumns";

const BRAND_HIDDEN_COLUMNS = ["retailerAccountManagerName", "brandName"];
const REP_HIDDEN_COLUMNS = ["newBadge", "retailerAccountManagerName", "repName", "status"];
const RETAILER_HIDDEN_COLUMNS = [
  "newBadge",
  "retailerAccountManagerName",
  "retailerName",
  "repCommissionPercent",
  "connectionSource",
];

type tTableState = {
  data: tBrandRetailerConnectionResponse[];
  isLoading: boolean;
  prev: string | null;
  next: string | null;
};

const formatDate = (dateInMilliseconds: number) => new Date(dateInMilliseconds).toLocaleDateString("en-US");

const getColorSchemeFromConnectionStatus = (status: tVisualConnectionStatus): tPdBadgeColorScheme => {
  if (status === "Declined") return "red";
  if (status === "Pending") return "yellow";
  if (status === "Invited") return "orange";
  return "green";
};

//

const CellRetailer: React.FC<{ row: tBrandRetailerConnectionResponse }> = ({ row }) => {
  const navigation = useNavigation();

  const onPress = () => {
    navigation.navigate(kRoute.P10, { id: row.retailerId, connectionId: row.id });
  };

  return (
    <Pressable onPress={onPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableCell
          style={[
            styles.tableCellRow,
            styles.tableColumnLg,
            hovered ? styles.rowHovered : {},
            pressed ? styles.rowPressed : {},
          ]}
        >
          <Image style={styles.profile} source={{ uri: row.retailerProfileUrl }} />
          <PdText numberOfLines={1}>{row.retailerName}</PdText>
        </PdTableCell>
      )}
    </Pressable>
  );
};

const CellBrand: React.FC<{ row: tBrandRetailerConnectionResponse }> = ({ row }) => {
  const navigation = useNavigation();

  const onPress = () => {
    navigation.navigate(kRoute.P100, { brand: row.brandName });
  };

  return (
    <Pressable onPress={onPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableCell
          style={[
            styles.tableCellRow,
            styles.tableColumnLg,
            hovered ? styles.rowHovered : {},
            pressed ? styles.rowPressed : {},
          ]}
        >
          <Image style={styles.profile} source={{ uri: row.brandProfileUrl }} />
          <PdText numberOfLines={1}>{row.brandName}</PdText>
        </PdTableCell>
      )}
    </Pressable>
  );
};

const CellRep: React.FC<{ row: tBrandRetailerConnectionResponse }> = ({ row }) => {
  const navigation = useNavigation();

  const onPress = () => {
    if (row.repName) {
      navigation.navigate(kRoute.P20, { id: row.repId, connectionId: row.id });
    }
  };

  return (
    <Pressable onPress={onPress}>
      {({ pressed, hovered }: tPressableState) => (
        <PdTableCell
          style={[
            styles.tableCellRow,
            styles.tableColumnLg,
            hovered ? styles.rowHovered : {},
            pressed ? styles.rowPressed : {},
          ]}
        >
          <Image style={styles.profile} source={{ uri: row.repProfileUrl }} />
          <PdText numberOfLines={1}>{row.repName || "-"}</PdText>
        </PdTableCell>
      )}
    </Pressable>
  );
};

const useTableColumnsUserConnections = () => {
  const userType = useSelector((state) => state.UserReducer.profile.accountType);
  return React.useMemo(() => {
    const columns: tColumn<tBrandRetailerConnectionResponse>[] = [
      {
        id: "newBadge",
        header: "",
        accessor: (originalRow) => originalRow.brandName,
        Cell: ({ row }) =>
          (row.status === "Active" || row.status === "Approved") &&
          moment()
            .startOf("day")
            .hour(12)
            .diff(moment(new Date(row.connectionDate)), "days") <= 14 && (
            <PdTag style={{ backgroundColor: kColors.brandGreen }}>
              <PdTagLabel style={{ color: kColors.white }}>NEW!</PdTagLabel>
            </PdTag>
          ),
        style: styles.tableColumnXs,
        alwaysVisible: true,
        isSortable: false,
        isHideable: false,
      },
      {
        id: "retailerAccountManagerName",
        header: "Account Manager",
        accessor: (originalRow) => originalRow.retailerAccountManagerName || "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "brandName",
        header: "Brand",
        accessor: (originalRow) => originalRow.brandName,
        Cell: CellBrand,
        style: styles.tableColumnLg,
      },
      {
        id: "retailerName",
        header: "Retailer",
        accessor: (originalRow) => originalRow.retailerName,
        Cell: CellRetailer,
        style: styles.tableColumnLg,
      },
      {
        id: "repName",
        header: "Rep",
        accessor: (originalRow) => originalRow.repName || "-",
        Cell: CellRep,
        style: styles.tableColumnLg,
      },
      {
        id: "repCommissionPercent",
        header: userType === "rep" ? "My Commission %" : "Commission %",
        accessor: (originalRow) =>
          originalRow.repName && originalRow.repCommissionPercent
            ? formatPercentage(originalRow.repCommissionPercent)
            : "-",
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "shipping",
        header: "Shipping %/No.",
        accessor: (originalRow) => originalRow.shipping,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "status",
        header: "Status",
        accessor: (originalRow) => originalRow.status,
        Cell: ({ value, row }) => (
          <PdBadge colorScheme={getColorSchemeFromConnectionStatus(row.status)}>
            <PdBadgeLabel>{value}</PdBadgeLabel>
          </PdBadge>
        ),
        style: styles.tableColumnSm,
      },
      {
        id: "connectionSource",
        header: "Source",
        accessor: (originalRow) => originalRow.connectionSource,
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
      {
        id: "connectionDate",
        header: "Connection Date",
        accessor: (originalRow) => formatDate(originalRow.connectionDate),
        Cell: ({ value }) => <PdTableCellText>{value}</PdTableCellText>,
        style: styles.tableColumnMd,
      },
    ];
    if (userType === "brand") return columns.filter((column) => !BRAND_HIDDEN_COLUMNS.includes(column.id));
    if (userType === "rep") return columns.filter((column) => !REP_HIDDEN_COLUMNS.includes(column.id));
    if (userType === "retailer") return columns.filter((column) => !RETAILER_HIDDEN_COLUMNS.includes(column.id));
    return columns;
  }, [userType]);
};

const ConnectionMobileRow: React.FC<{ item: tRowInstance<tBrandRetailerConnectionResponse> }> = ({ item }) => {
  const { isAdmin, isBrand, isRetailer, isRep } = useUserProfile();
  const navigation = useNavigation();

  const handleOnPressRetailer = () => {
    navigation.navigate(kRoute.P10, { id: item.retailerId, connectionId: item.id });
  };

  const handleOnPressBrand = () => {
    navigation.navigate(kRoute.P100, { brand: item.brandName });
  };

  const handleOnPressRep = () => {
    if (item.repName) {
      navigation.navigate(kRoute.P20, { id: item.repId, connectionId: item.id });
    }
  };

  if (isRep) {
    return (
      <PdTableRowMobile style={styles.mobileRow}>
        <View style={styles.rowMobileContainer}>
          <TouchableOpacity onPress={handleOnPressRetailer}>
            <PdText style={styles.orderMobileRowOrderNumber}>{item.retailerName}</PdText>
          </TouchableOpacity>
          <TouchableOpacity onPress={handleOnPressBrand}>
            <PdText style={styles.orderMobileRowOrderNumber}>{item.brandName}</PdText>
          </TouchableOpacity>
        </View>
        <PdText>{`Commission: ${item.repCommissionPercent ? formatPercentage(item.repCommissionPercent) : ""}`}</PdText>
      </PdTableRowMobile>
    );
  }

  return (
    <PdTableRowMobile style={styles.mobileRow}>
      <View style={styles.rowMobileContainer}>
        {(isAdmin || isBrand) && (
          <TouchableOpacity onPress={handleOnPressRetailer}>
            <PdText style={styles.orderMobileRowOrderNumber}>{item.retailerName}</PdText>
          </TouchableOpacity>
        )}
        {(isAdmin || isRetailer) && (
          <TouchableOpacity onPress={handleOnPressBrand}>
            <PdText style={styles.orderMobileRowOrderNumber}>{item.brandName}</PdText>
          </TouchableOpacity>
        )}
        <PdText>{`Source: ${item.connectionSource}`}</PdText>
        <TouchableOpacity onPress={handleOnPressRep}>
          <PdText>{`Rep: ${item.repName || "N/A"}`}</PdText>
        </TouchableOpacity>
      </View>
      <PdBadge colorScheme={getColorSchemeFromConnectionStatus(item.status)} style={styles.mobileRowContainerBadge}>
        <PdBadgeLabel>{item.status}</PdBadgeLabel>
      </PdBadge>
    </PdTableRowMobile>
  );
};

const ConnectionRow: React.FC<{ item: tRowInstance<tBrandRetailerConnectionResponse> }> = ({ item }) => {
  return (
    <PdTableRow>
      {item.cells.map((cell) => (
        <PdTableCell key={cell.uniqueId} style={cell.style}>
          {cell.render("Cell")}
        </PdTableCell>
      ))}
    </PdTableRow>
  );
};

const P06_Connections: React.FC = () => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rAccountType = useSelector((state) => state.UserReducer.profile.accountType);
  const rCompanyId = useSelector((state) => state.UserReducer.profile.companyRefPath.split("/")?.[1]);
  const rBrandDefaultShipping = useSelector((state) =>
    state.CompanyReducer.type === "brand" ? state.CompanyReducer.defaultShipPercent : undefined,
  );
  const rTableConfigs = useSelector((state) => state.TableConfigReducer.connections);
  const { isAdmin, isBrand, isRetailer, isRep } = useUserProfile();
  const inviteRetailersModal = useInviteRetailersModal();
  const addRetailerModal = useAddRetailerModal();
  const adminConnectCustomers = useAdminConnectCustomers();
  const filters = useSelector((state) => state.FiltersReducer.connections);
  const dTermFilter = useDebounce(filters.term, 500);
  const [tableState, setTableState] = React.useState<tTableState>({
    isLoading: true,
    data: [],
    prev: null,
    next: null,
  });
  const isFocused = useIsFocused();
  const columns = useTableColumnsUserConnections();
  const tableInstance = useTable<tBrandRetailerConnectionResponse>({
    data: tableState.data,
    columns,
    initialState: {
      sortedBy: "connectionDate",
      sortDirection: "desc",
      hiddenColumns: getHiddenColumnsFromConfig(rTableConfigs),
    },
  });

  const handleOnPressInvite = async () => {
    inviteRetailersModal.openAsync({ companyId: rCompanyId, source: "brand" });
  };

  const handleOnPressAddRetailer = () => {
    addRetailerModal.openAsync({
      companyId: rCompanyId,
      brandDefaultShippingFee: rBrandDefaultShipping,
      source: "brand",
    });
  };

  const handleOnPressConnectCustomers = () => {
    adminConnectCustomers.openAsync();
  };

  const handleOnPressCopyAsCSV = async () => {
    try {
      await navigator.clipboard.writeText(generateCSVFromTableInstance(tableInstance.rows, tableInstance.headers));
      dispatch({ type: "RIBBON", message: "Copied to clipboard" });
    } catch (er) {
      dispatch({ type: "RIBBON", message: new Error("Could not copy to clipboard") });
    }
  };

  const handleOnPressPrevPage = () => {
    dispatch(changeFilterPrevPage({ pageName: "connections", value: tableState?.prev ?? "" }));
  };

  const handleOnPressNextPage = () => {
    dispatch(changeFilterNextPage({ pageName: "connections", value: tableState?.next ?? "" }));
  };

  const updateHiddenColumns = (newTableConfig: Record<keyof tBrandRetailerConnectionResponse, boolean>) => {
    const newHiddenColumns = getHiddenColumnsFromConfig(newTableConfig);
    tableInstance.setHiddenColumns(newHiddenColumns);
  };

  const renderItem: React.FC<{ item: tRowInstance<tBrandRetailerConnectionResponse> }> = React.useCallback(
    ({ item }) => {
      if (isMobile) return <ConnectionMobileRow item={item} />;
      return <ConnectionRow item={item} />;
    },
    [isMobile],
  );

  React.useEffect(() => {
    let unsubscribe: (() => void) | undefined;

    (async () => {
      const params: connectionDb.tQueryBrandRetailerConnectionsParams = { term: dTermFilter };
      if (filters.connectionStatus) params.status = formatDatabaseConnectionStatus(filters.connectionStatus);
      if (filters.connectionSource === "Direct") {
        params.source = "Direct";
        params.isSalesRep = false;
      }
      if (filters.connectionSource === "The Main Tab") {
        params.source = "TMT";
        params.isSalesRep = false;
      }
      if (filters.connectionSource === "Sales Rep") params.isSalesRep = true;
      if (rAccountType === "brand") params.brandId = rCompanyId;
      if (rAccountType === "retailer") params.retailerId = rCompanyId;
      if (rAccountType === "rep") params.repId = rCompanyId;
      const query = await utilsDb.withPagination(
        connectionDb.queryBrandRetailerConnection,
        connectionDb.documentBrandRetailerConnection,
        {
          ...params,
          prev: filters.prev,
          next: filters.next,
          limit: kTablePageSize,
        },
      );

      unsubscribe = query.onSnapshot((snapshot) => {
        const data = snapshot.docs.map((doc) => mappers.mapBrandRetailerConnection(doc.data()));
        setTableState({
          isLoading: false,
          data,
          prev: data[0]?.id ?? "",
          next: data.length >= kTablePageSize ? data[data.length - 1]?.id ?? "" : "",
        });
      });
    })();

    return () => {
      unsubscribe?.();
    };
  }, [
    dTermFilter,
    filters.connectionSource,
    filters.connectionStatus,
    filters.next,
    filters.prev,
    rAccountType,
    rCompanyId,
  ]);

  React.useEffect(() => {
    if (!isFocused) {
      dispatch(resetFilters());
    }
  }, [dispatch, isFocused]);

  if (tableState.isLoading) {
    return (
      <PdCenter>
        <ActivityIndicator size="large" color={kColors.brandGreen} />
      </PdCenter>
    );
  }

  return (
    <View style={isMobile ? styles.mobilePage : styles.page}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} style={[styles.justifyBetween, styles.mb4]}>
        <PdText variant="subheader">
          {isBrand ? "My Customers" : isRep ? "My Connections" : isRetailer ? "My Brands" : "Connections"}
        </PdText>
        {isBrand && (
          <PdStack direction={isMobile ? "vertical" : "horizontal"}>
            <PdButton
              colorScheme="purple"
              leftIcon={<PdUserAdd color="black" style={styles.buttonIcon} />}
              onPress={handleOnPressInvite}
            >
              <PdButtonLabel>Invite your Retailers</PdButtonLabel>
            </PdButton>
            <PdButton onPress={handleOnPressAddRetailer} leftIcon={<PdStorefront style={styles.buttonIcon} />}>
              <PdButtonLabel>Add a Retailer</PdButtonLabel>
            </PdButton>
          </PdStack>
        )}
        {isAdmin && (
          <PdButton onPress={handleOnPressConnectCustomers} leftIcon={<Link style={styles.buttonIcon} />}>
            <PdButtonLabel>Connect Customers</PdButtonLabel>
          </PdButton>
        )}
      </PdStack>
      <PdSearchInput
        style={styles.mb4}
        value={filters.term}
        onChangeText={(value) => dispatch(changeFilterTerm({ pageName: "connections", value }))}
        placeholder="Search Customers"
      />
      {(isBrand || isRetailer || isAdmin) && (
        <PdStack direction={isMobile ? "vertical" : "horizontal"} style={styles.filtersContainer}>
          <View style={styles.filtersStatusContainer}>
            <PdFormLabel>Status</PdFormLabel>
            <PdTab
              isActive={!filters.connectionStatus}
              onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections" }))}
            >
              <PdTabLabel>All</PdTabLabel>
            </PdTab>
            <PdTab
              isActive={filters.connectionStatus === "Active"}
              onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Active" }))}
            >
              <PdTabLabel>Active</PdTabLabel>
            </PdTab>
            <PdTab
              isActive={filters.connectionStatus === "Pending"}
              onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Pending" }))}
            >
              <PdTabLabel>Pending</PdTabLabel>
            </PdTab>
            <PdTab
              isActive={filters.connectionStatus === "Invited"}
              onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Invited" }))}
            >
              <PdTabLabel>Invited</PdTabLabel>
            </PdTab>
            <PdTab
              isActive={filters.connectionStatus === "Declined"}
              onPress={() => dispatch(changeFilterConnectionStatus({ pageName: "connections", value: "Declined" }))}
            >
              <PdTabLabel>Declined</PdTabLabel>
            </PdTab>
          </View>
          {(isBrand || isAdmin) && (
            <View style={styles.filtersStatusContainer}>
              <PdFormLabel>Source</PdFormLabel>
              <PdTab
                isActive={!filters.connectionSource}
                onPress={() => dispatch(changeFilterConnectionSource({ pageName: "connections" }))}
              >
                <PdTabLabel>All</PdTabLabel>
              </PdTab>
              <PdTab
                isActive={filters.connectionSource === "Direct"}
                onPress={() => dispatch(changeFilterConnectionSource({ pageName: "connections", value: "Direct" }))}
              >
                <PdTabLabel>Direct</PdTabLabel>
              </PdTab>
              <PdTab
                isActive={filters.connectionSource === "The Main Tab"}
                onPress={() =>
                  dispatch(changeFilterConnectionSource({ pageName: "connections", value: "The Main Tab" }))
                }
              >
                <PdTabLabel>The Main Tab</PdTabLabel>
              </PdTab>
              <PdTab
                isActive={filters.connectionSource === "Sales Rep"}
                onPress={() => dispatch(changeFilterConnectionSource({ pageName: "connections", value: "Sales Rep" }))}
              >
                <PdTabLabel>Sales Rep</PdTabLabel>
              </PdTab>
            </View>
          )}
        </PdStack>
      )}
      {!isMobile && (
        <PdStack style={styles.tableOptionsContainer}>
          <PdButton
            colorScheme="gray"
            size="sm"
            rightIcon={<PdCopy style={styles.iconSm} />}
            onPress={handleOnPressCopyAsCSV}
          >
            <PdButtonLabel>Copy as CSV</PdButtonLabel>
          </PdButton>
          <PdPopover offset={22 + 2 * kStyleConst.kSpacing}>
            <PdPopoverTrigger>
              <PdButton colorScheme="gray" size="sm" rightIcon={<PdSettings style={styles.iconSm} />}>
                <PdButtonLabel>Configure Table</PdButtonLabel>
              </PdButton>
            </PdPopoverTrigger>
            <PdPopoverContent style={styles.configureTablePopoverContent}>
              <PopoverContentTableColumns
                columns={columns}
                tableConfigReduxKey="connections"
                onPressCheckbox={updateHiddenColumns}
              />
            </PdPopoverContent>
          </PdPopover>
        </PdStack>
      )}
      <PdTable
        ListHeaderComponent={!isMobile ? <PdTableSortableHeaders headers={tableInstance.headers} /> : null}
        ItemSeparatorComponent={() => (isMobile ? <PdTableMobileItemSeparator /> : null)}
        data={tableInstance.rows}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        isMobile={isMobile}
      />
      <PdTablePaginationFooter
        page={filters.page}
        hasNext={!!tableState?.next}
        onPressBack={handleOnPressPrevPage}
        onPressNext={handleOnPressNextPage}
      />
    </View>
  );
};

export const P06 = React.memo(P06_Connections);

const styles = StyleSheet.create({
  buttonIcon: {
    height: 14,
    width: 14,
  },
  configureTablePopoverContent: {
    backgroundColor: kColors.white,
    borderColor: kColors.gray300,
    borderRadius: 5,
    borderWidth: 1,
    left: -150,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingVertical: kStyleConst.kSpacing * 3,
    right: 0,
  },
  filtersContainer: {
    alignItems: "flex-start",
    flexWrap: "wrap",
    marginBottom: kStyleConst.kSpacing * 4,
  },
  filtersStatusContainer: {
    alignItems: "center",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 2 * kStyleConst.kSpacing,
  },
  iconSm: {
    height: 11,
    width: 11,
  },
  justifyBetween: {
    justifyContent: "space-between",
  },
  mb4: {
    marginBottom: kStyleConst.kSpacing * 4,
  },
  mobilePage: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 2,
    paddingHorizontal: kStyleConst.kSpacing * 4,
    paddingTop: kStyleConst.kSpacing * 4,
  },
  mobileRow: {
    alignItems: "center",
    flexDirection: "row",
    padding: kStyleConst.kSpacing * 2,
  },
  mobileRowContainerBadge: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  orderMobileRowOrderNumber: {
    fontSize: 16,
    fontWeight: "500",
  },
  page: {
    flex: 1,
    paddingBottom: kStyleConst.kSpacing * 4,
    paddingHorizontal: kStyleConst.kSpacing * 10,
    paddingTop: kStyleConst.kSpacing * 8,
  },
  profile: {
    backgroundColor: kColors.lightMed,
    borderRadius: kStyleConst.kTableProfileImageDimension / 2,
    height: kStyleConst.kTableProfileImageDimension,
    marginEnd: kStyleConst.kSpacing * 2,
    width: kStyleConst.kTableProfileImageDimension,
  },
  rowHovered: {
    backgroundColor: kColors.gray50,
  },
  rowMobileContainer: {
    flex: 1,
  },
  rowPressed: {
    opacity: 0.6,
  },
  tableCellRow: {
    alignItems: "center",
    flexDirection: "row",
    width: "100%",
  },
  tableColumnLg: {
    width: 300,
  },
  tableColumnMd: {
    width: 160,
  },
  tableColumnSm: {
    width: 100,
  },
  tableColumnXs: {
    width: 70,
  },
  tableOptionsContainer: {
    marginBottom: kStyleConst.kSpacing * 2,
    marginLeft: "auto",
    zIndex: 1,
  },
});
